import React, { useState, useEffect } from 'react';
import dynamicPricingService from '../services/dynamicPricingService';
import LoadingSpinner from './LoadingSpinner';
import ConfirmModal from './ConfirmModal';

const UNIT_TYPES = {
  'piece': 'Peça',
  'meter': 'Metro',
  'm2': 'Metro Quadrado',
  'm3': 'Metro Cúbico',
  'kg': 'Quilograma'
};

const DynamicPricingManager = ({ costId, costData, onPricingChange }) => {
  const [basePrices, setBasePrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Formulário de preço base
  const [formData, setFormData] = useState({
    thickness_mm: '',
    color: '',
    width_cm: '',
    height_cm: '',
    length_cm: '',
    brand: '',
    model: '',
    price_per_unit: '',
    unit_type: 'piece'
  });

  // Dados para cálculo
  const [calculationData, setCalculationData] = useState({
    thickness_mm: '',
    color: '',
    width_cm: '',
    height_cm: '',
    length_cm: '',
    brand: '',
    model: '',
    quantity: 1
  });

  useEffect(() => {
    if (costId) {
      loadBasePrices();
    }
  }, [costId]);

  const loadBasePrices = async () => {
    try {
      setLoading(true);
      const response = await dynamicPricingService.getBasePrices(costId);
      setBasePrices(response.data);
    } catch (error) {
      setError('Erro ao carregar preços base');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculationChange = (e) => {
    const { name, value } = e.target;
    setCalculationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPrice) {
        await dynamicPricingService.updateBasePrice(editingPrice.id, formData);
      } else {
        await dynamicPricingService.createBasePrice(costId, formData);
      }

      setShowForm(false);
      setEditingPrice(null);
      resetForm();
      loadBasePrices();
      if (onPricingChange) onPricingChange();
    } catch (error) {
      setError('Erro ao salvar preço base');
      console.error(error);
    }
  };

  const handleEdit = (price) => {
    setEditingPrice(price);
    setFormData({
      thickness_mm: price.thickness_mm || '',
      color: price.color || '',
      width_cm: price.width_cm || '',
      height_cm: price.height_cm || '',
      length_cm: price.length_cm || '',
      brand: price.brand || '',
      model: price.model || '',
      price_per_unit: price.price_per_unit,
      unit_type: price.unit_type
    });
    setShowForm(true);
  };

  const handleDelete = async (priceId) => {
    setConfirmAction(() => async () => {
      try {
        await dynamicPricingService.deleteBasePrice(priceId);
        loadBasePrices();
        if (onPricingChange) onPricingChange();
      } catch (error) {
        setError('Erro ao remover preço base');
        console.error(error);
      }
    });
    setShowConfirmModal(true);
  };

  const handleCalculatePrice = async () => {
    try {
      const response = await dynamicPricingService.calculateDynamicPrice(costId, calculationData);
      setCalculationResult(response.data);
    } catch (error) {
      setError('Erro ao calcular preço');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      thickness_mm: '',
      color: '',
      width_cm: '',
      height_cm: '',
      length_cm: '',
      brand: '',
      model: '',
      price_per_unit: '',
      unit_type: 'piece'
    });
  };

  const handleNewPrice = () => {
    setEditingPrice(null);
    resetForm();
    setShowForm(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDimensions = (price) => {
    const dimensions = [];
    if (price.width_cm) dimensions.push(`${price.width_cm}cm`);
    if (price.height_cm) dimensions.push(`${price.height_cm}cm`);
    if (price.length_cm) dimensions.push(`${price.length_cm}cm`);
    return dimensions.length > 0 ? dimensions.join(' x ') : '-';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Calculadora de Preços Dinâmicos */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">🧮 Calculadora de Preços Dinâmicos</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Espessura (mm)
            </label>
            <input
              type="number"
              name="thickness_mm"
              value={calculationData.thickness_mm}
              onChange={handleCalculationChange}
              step="0.1"
              placeholder="Ex: 15, 18, 25"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cor
            </label>
            <input
              type="text"
              name="color"
              value={calculationData.color}
              onChange={handleCalculationChange}
              placeholder="Ex: Branco, Nogal, Caramelo"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantidade
            </label>
            <input
              type="number"
              name="quantity"
              value={calculationData.quantity}
              onChange={handleCalculationChange}
              min="0.1"
              step="0.1"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Largura (cm)
            </label>
            <input
              type="number"
              name="width_cm"
              value={calculationData.width_cm}
              onChange={handleCalculationChange}
              step="0.1"
              placeholder="Ex: 275"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Altura (cm)
            </label>
            <input
              type="number"
              name="height_cm"
              value={calculationData.height_cm}
              onChange={handleCalculationChange}
              step="0.1"
              placeholder="Ex: 185"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comprimento (cm)
            </label>
            <input
              type="number"
              name="length_cm"
              value={calculationData.length_cm}
              onChange={handleCalculationChange}
              step="0.1"
              placeholder="Ex: 50"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marca
            </label>
            <input
              type="text"
              name="brand"
              value={calculationData.brand}
              onChange={handleCalculationChange}
              placeholder="Ex: Hettich, Blum, Padrão"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Modelo
            </label>
            <input
              type="text"
              name="model"
              value={calculationData.model}
              onChange={handleCalculationChange}
              placeholder="Ex: Moderno, Luxo, Simples"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <button
          onClick={handleCalculatePrice}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          🧮 Calcular Preço Dinâmico
        </button>
      </div>

      {/* Resultado do Cálculo */}
      {calculationResult && (
        <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-green-800 mb-4">💰 Resultado do Cálculo Dinâmico</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="font-medium text-green-700 mb-2">Detalhamento:</h5>
              <ul className="space-y-1 text-sm text-green-600">
                <li>Preço base: {formatCurrency(calculationResult.calculation_breakdown.base_price)}</li>
                <li>Após multiplicadores: {formatCurrency(calculationResult.calculation_breakdown.after_multipliers)}</li>
                <li>Após quantidade: {formatCurrency(calculationResult.calculation_breakdown.after_quantity)}</li>
                <li>Com margem ({calculationResult.margin_percentage}%): {formatCurrency(calculationResult.calculation_breakdown.with_margin)}</li>
              </ul>
              <div className="mt-2 text-sm text-green-600">
                <strong>Multiplicador total: {calculationResult.total_multiplier.toFixed(4)}x</strong>
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-800">
                {formatCurrency(calculationResult.selling_price)}
              </div>
              <div className="text-sm text-green-600">Preço de Venda Final</div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Preços Base */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">
            💰 Preços Base por Características ({basePrices.length})
          </h3>
          <button
            onClick={handleNewPrice}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            ➕ Novo Preço Base
          </button>
        </div>

        {/* Formulário de Preço Base */}
        {showForm && (
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h4 className="text-md font-semibold mb-4">
              {editingPrice ? '✏️ Editar Preço Base' : '➕ Novo Preço Base'}
            </h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Espessura (mm)
                  </label>
                  <input
                    type="number"
                    name="thickness_mm"
                    value={formData.thickness_mm}
                    onChange={handleInputChange}
                    step="0.1"
                    placeholder="Ex: 15"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cor
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    placeholder="Ex: Branco"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Largura (cm)
                  </label>
                  <input
                    type="number"
                    name="width_cm"
                    value={formData.width_cm}
                    onChange={handleInputChange}
                    step="0.1"
                    placeholder="Ex: 275"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Altura (cm)
                  </label>
                  <input
                    type="number"
                    name="height_cm"
                    value={formData.height_cm}
                    onChange={handleInputChange}
                    step="0.1"
                    placeholder="Ex: 185"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comprimento (cm)
                  </label>
                  <input
                    type="number"
                    name="length_cm"
                    value={formData.length_cm}
                    onChange={handleInputChange}
                    step="0.1"
                    placeholder="Ex: 50"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marca
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    placeholder="Ex: Hettich"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modelo
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="Ex: Moderno"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preço por Unidade (R$) *
                  </label>
                  <input
                    type="number"
                    name="price_per_unit"
                    value={formData.price_per_unit}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required
                    placeholder="0.00"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Unidade *
                  </label>
                  <select
                    name="unit_type"
                    value={formData.unit_type}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(UNIT_TYPES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingPrice(null);
                    resetForm();
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingPrice ? 'Atualizar' : 'Criar'} Preço Base
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Características
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dimensões
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço/Unidade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {basePrices.map((price) => (
                <tr key={price.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1 text-sm">
                      {price.thickness_mm && <div>📏 {price.thickness_mm}mm</div>}
                      {price.color && <div>🎨 {price.color}</div>}
                      {price.brand && <div>🏷️ {price.brand}</div>}
                      {price.model && <div>🔧 {price.model}</div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDimensions(price)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(price.price_per_unit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {UNIT_TYPES[price.unit_type]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(price)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => handleDelete(price.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      🗑️ Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {basePrices.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum preço base cadastrado. Crie um preço base para começar a usar preços dinâmicos.
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={showConfirmModal}
        message="Tem certeza que deseja remover este preço base?"
        onConfirm={() => {
          if (confirmAction) confirmAction();
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
      />
    </div>
  );
};

export default DynamicPricingManager;
